import { Dashboard as DashboardIcon } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Header } from 'src/components/header';
import { SimpleDialog } from 'src/components/modal';
import { ProductForm } from 'src/components/productForm';
import { ProductVariationForm } from 'src/components/productVariationForm';
import { Snackbar } from 'src/components/snackbar';
import { UploadImageButton } from 'src/components/uploadImageButton';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import {
  useGetCCAssets,
  useGetProduct,
  useGetThemes,
  useModal,
  useUpdateProduct,
  useViewAssetsWithComponents,
} from 'src/hooks';
import { useApiErrorNavigator, useNavigateBlocker } from 'src/hooks/navigation';
import { IProduct, IProductVariation } from 'src/interfaces';
import { hasUniqueSizeAndFlavorVariations, lang, useUserRole } from 'src/utils';

import {
  Container,
  Content,
  Divider,
  LeftContainer,
  Section,
  StyledDiscardButton,
} from '../addProductPage/AddProductPage.styled';
import { useEditProductPage } from './hooks/EditProductPage.hook';

export const EditProductPage = () => {
  const showSavedModal = useModal();
  const discardModal = useModal();
  const saveProductModal = useModal();
  const updateProduct = useUpdateProduct();
  const { data: themes } = useGetThemes();
  const { data: allVariationAssets } = useGetCCAssets();
  const { productCode = '' } = useParams();
  const {
    data: productData,
    isFetched: isProductFetched,
    error,
  } = useGetProduct(productCode.toString());
  const { isViewOnly } = useUserRole();

  const { data: viewAssetsWithComponents } = useViewAssetsWithComponents();

  const { formMethod, resetFormValue, shouldBlock, skipNextBlock } =
    useEditProductPage(
      productData,
      allVariationAssets,
      viewAssetsWithComponents,
    );

  const productAsset =
    formMethod.watch('asset') ||
    allVariationAssets?.find(
      (asset) => productData?.asset_id?.[0] === asset.id,
    );

  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [newVariationIndex, setNewVariationIndex] = useState(-1);
  const productVariations = formMethod.getValues().productVariations;

  useApiErrorNavigator(error);
  useNavigateBlocker(shouldBlock);

  const onAddVariation = () => {
    // Check if 'Product Code' is empty
    const LAST = -1;
    const productValue = formMethod.getValues().productVariations;
    const lastProductCode = productValue.at(LAST)?.product_code;

    if (!lastProductCode?.trim()) {
      Snackbar.error(lang.productCodeMissing);

      return;
    }

    setNewVariationIndex(productVariations.length);
    productVariations?.push({
      active: true,
      nutrition: '000',
      volume: 0,
    } as unknown as IProductVariation);

    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight - 900,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const onSaved = async (values: IProduct & { asset?: File }) => {
    const trimmedNameOverride = values.name_override
      ? values.name_override.trim()
      : null;

    // Trim the name field for each product variation
    const trimmedProductVariations = values.productVariations.map(
      (variation) => ({
        ...variation,
        name: variation.name.trim(),
      }),
    );

    /* Shouldn't have more than one variation with the same size same flavor */
    const isValid = hasUniqueSizeAndFlavorVariations(trimmedProductVariations);

    if (!isValid) {
      Snackbar.error('Product variation size and flavor must be unique');

      return;
    }

    // Update the values with trimmed fields
    const trimmedValues = {
      ...values,
      name_override: trimmedNameOverride,
      productVariations: trimmedProductVariations,
      allVariationAssets: allVariationAssets ?? [],
    };
    await updateProduct.mutateAsync(trimmedValues);

    setNewVariationIndex(-1);
    saveProductModal.onCloseModal();

    Snackbar.success(`${values.product_code} ${lang.successfullyUpdated}`);

    showSavedModal.onOpenModal();
  };

  const onCloseNavigation = () => {
    showSavedModal.onCloseModal();

    resetFormValue();
  };

  const handleSaveConfirm = formMethod.handleSubmit((values) => {
    saveProductModal.onOpenModal(() => onSaved(values));
  });

  const handleValidateSelectAsset = (groupImageName: string) => {
    const variationNamePresent = productVariations?.some((obj) =>
      obj.variationAssets?.some((asset) => asset.assetName === groupImageName),
    );

    if (variationNamePresent) {
      Snackbar.warning(
        lang.productVariationValidateSelectAssetFailed(groupImageName),
      );

      return false;
    }

    return true;
  };

  const handleDiscard = () => {
    if (shouldBlock()) {
      discardModal.onOpenModal();

      return;
    }

    skipNextBlock();
    navigate(PATHS.PRODUCTS);
  };

  return isViewOnly ? (
    <Navigate to={PATHS.PRODUCTS} replace />
  ) : isProductFetched && productData?.productVariations?.length === 0 ? (
    <Navigate to={PATHS.NOT_FOUND} replace /> // Redirect to 404
  ) : (
    <FormProvider {...formMethod}>
      <Container>
        <Header
          breadcrumbItems={[
            {
              label: lang.products,
              link: PATHS.PRODUCTS,
            },
            {
              label: lang.editProduct,
            },
          ]}
          title={lang.editProduct}
          actionBar={isViewOnly && <ViewOnlyAlert />}
        />

        <Content ref={containerRef}>
          <Section>
            <LeftContainer>
              <UploadImageButton
                type="editProduct"
                handleValidateSelectAsset={handleValidateSelectAsset}
              />
            </LeftContainer>

            <ProductForm productData={productData} />
          </Section>

          {productVariations?.map((productVariation, index) => (
            <Box key={`${productVariation.modified_at}-${index}`}>
              <Divider />

              <Section>
                <LeftContainer />
                <ProductVariationForm
                  productVariation={productVariation}
                  themes={themes}
                  index={index}
                  isProductCodeEnabled={index === newVariationIndex}
                  groupProductImage={productAsset}
                  resetFormValue={resetFormValue}
                  onBeforeDyNavigation={skipNextBlock}
                />
              </Section>
            </Box>
          ))}
        </Content>

        <Divider />

        {!isViewOnly && (
          <Section>
            <Button
              startIcon={<DashboardIcon />}
              variant="outlined"
              onClick={onAddVariation}
              disabled={newVariationIndex !== -1}>
              {lang.addVariation}
            </Button>

            <Section sx={{ gap: '10px' }}>
              <StyledDiscardButton variant="text" onClick={handleDiscard}>
                {lang.discard}
              </StyledDiscardButton>
              <Button disabled={isViewOnly} onClick={handleSaveConfirm}>
                {lang.save}
              </Button>
            </Section>
          </Section>
        )}
      </Container>

      {discardModal.open && (
        <SimpleDialog
          actionColor="error"
          open={discardModal.open}
          onClose={discardModal.onCloseModal}
          onClickCancel={discardModal.onCloseModal}
          onClickConfirm={() => {
            skipNextBlock();
            navigate(PATHS.PRODUCTS);
          }}
          title={lang.discardModal.title}
          subTitle={lang.discardModal.subTitle}
          cancelText={lang.cancel}
          confirmText={lang.discard}
        />
      )}
      {saveProductModal.open && (
        <SimpleDialog
          open={saveProductModal.open}
          onClose={saveProductModal.onCloseModal}
          onClickCancel={saveProductModal.onCloseModal}
          onClickConfirm={saveProductModal.customFn}
          title={lang.titleTextSave}
          cancelText={lang.cancel}
          confirmText={lang.confirm}
          isLoading={updateProduct.isLoading}
          modalSize="sm">
          <Typography gutterBottom>{lang.subTitlePublishConfirm}</Typography>
        </SimpleDialog>
      )}
      {showSavedModal && (
        <SimpleDialog
          open={showSavedModal.open}
          onClose={onCloseNavigation}
          modalSize="sm"
          title={lang.titleTextAfterSave}
          cancelText={lang.close}
          confirmText={lang.goToPublications}
          onClickCancel={onCloseNavigation}
          onClickConfirm={() => {
            skipNextBlock();
            navigate(PATHS.PRODUCT_PUBLICATIONS);
          }}>
          <Typography gutterBottom>
            {lang.subTitleConfirmProductPublication}
          </Typography>
        </SimpleDialog>
      )}
    </FormProvider>
  );
};
